<template>
  <section
    v-if="!preventFetchByCat"
    :key="tick"
    class="member-demo-module curated-features featured-post-links"
  >
    <div container>
      <div
        v-if="section.title || showCategorySelect"
        grid="row wrap"
        class="intro-content _mbs">
        
        <div :column="showCategorySelect && !postsExist ? 'xs-12 s-12 m-9' : 'xs-12 s-12'">
          <h2
            v-if="section.title"
            :style="globalTheme.colorText"
            class="intro-title">
            {{ section.title }}
            <app-inline-svg
              v-if="section.titleSvgIcon"
              class="section-title-svg _mlxs"
              :style="globalTheme.fillText"
              :src="section.titleSvgIcon.url" />
          </h2>
        </div>

        <div
          v-if="showCategorySelect"
          column="xs-12 s-12"
          class="mobile until-tablet">
          <category-selector
            :current-category="referencedCategory"
            @newCategorySelected="updateSelectedCategory"/>  
        </div>

        <div
          v-if="showCategorySelect && !postsExist"
          column="xs-12 s-12 m-3">
          <category-selector
            :current-category="referencedCategory"
            @newCategorySelected="updateSelectedCategory"/>  
        </div>

      </div>
      <app-loader v-if="fetching"/>
      <p
        v-else-if="showCategorySelect && !postsExist"
        class="_mtl _text-center"> 
        {{noPostsMessage}} 
      </p>
      <div
        v-else
        grid="row wrap justify-center">
        <div
          v-if="firstFeature"
          column="xs-12 s-12 m-8"
          class="">
          <curated-feature
            :category="categoryTitle"
            :post="firstFeature"
            first
          />
        </div>
        <div
          v-if="curatedFeaturesSide.length"
          column="xs-12 s-12 m-4"
          class="">
          <div
            grid="row wrap"
            class="side-features">
            <div
              v-if="showCategorySelect"
              column="xs-12 s-12 m-12"
              class="_mbs tablet-up">
              <category-selector
                :current-category="referencedCategory"
                @newCategorySelected="updateSelectedCategory"/>
            </div>
            <div
              v-for="(post, i) in curatedFeaturesSide"
              :key="i"
              column="xs-12 s-12 m-12"
            >
              <curated-feature :post="post" />
            </div>
            <div
              v-if="!curatedFeaturesRest.length"
              column="xs-12 s-12"
              class="_text-center"
            >
              <localized-router-link
                v-if="section.seeMoreCta && section.seeMoreCta.title && section.seeMoreCta.href"
                class="see-more-cta"
                :to="section.seeMoreCta.href"
                :style="globalTheme.colorText"
              >
                {{ section.seeMoreCta.title }}
              </localized-router-link>
            </div>
          </div>
        </div>
        <template v-if="curatedFeaturesRest.length">
          <div
            v-for="(post, i) in curatedFeaturesRest"
            :key="i"
            column="xs-12 s-12 m-4"
            class=""
          >
            <curated-feature :post="post" />
          </div>
          <div
            column="xs-12 s-12 m-4"
            class="_text-center">
            <localized-router-link
              v-if="section.seeMoreCta && section.seeMoreCta.title && section.seeMoreCta.href"
              class="see-more-cta"
              :to="section.seeMoreCta.href"
              :style="globalTheme.colorText"
            >
              {{ section.seeMoreCta.title }}
            </localized-router-link>
          </div>
        </template>
        <div
          v-if="
            section.seeMoreCta &&
              section.seeMoreCta.title &&
              section.seeMoreCta.href &&
              !curatedFeaturesRest.length &&
              !curatedFeaturesSide.length
          "
          column="xs-12 s-12 m-6"
          class="_text-center"
        >
          <localized-router-link
            class="see-more-cta"
            :to="section.seeMoreCta.href"
            :style="globalTheme.colorText"
          >
            {{ section.seeMoreCta.title }}
          </localized-router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
  import cloneDeep from 'lodash.clonedeep';

  import { defineComponent } from 'vue'
  import { ContentStackPost } from '@/types'

  import {
    postsState,
    FeaturedPostsSection,
    SiftedPosts
  } from '@/mixins/FeaturedPostsModuleState'

  import {
    CuratedFeatureProps,
    curatedFeatureFactory
  } from '@/components/sections/FeaturedPosts/CuratedFeature.vue'

  import CuratedFeature from '@/components/sections/FeaturedPosts/CuratedFeature.vue'

  interface CuratedFeatureSections extends FeaturedPostsSection {
    manualEntries: CuratedFeatureProps[];
  }

  export default defineComponent({
    name: 'CuratedFeatures' as string,
    components: { CuratedFeature },
    mixins: [postsState],
    props: {
      section: {
        type: Object as () => CuratedFeatureSections,
        required: true
      }
    },
    data() {
      return {
        readMoreLabel: this.$store.state.globals.globalLabels.readMore
      }
    },
    computed: {
      categoryTitle(): string | null {
        return this.referencedCategory  ? this.referencedCategory .title : null
      },
      firstFeature(): CuratedFeatureProps | undefined {
        const postsToDisplay = (this as any).postsToDisplay as SiftedPosts
        let allPosts
        if (postsToDisplay.referencedPostList) {
          allPosts = (postsToDisplay.posts as ContentStackPost[]).map(refPost =>
            curatedFeatureFactory(refPost, this.readMoreLabel)
          )
        } else {
          allPosts = postsToDisplay.posts as CuratedFeatureProps[]
        }
        const firstFeature = allPosts[0]
        if (firstFeature) {
          firstFeature.blurb = this.readMoreLabel
        }
        return firstFeature
      },
      curatedFeaturesSide(): CuratedFeatureProps[] {
        const allPosts = (this as any).postsToDisplay.posts
        const referencedPostList = (this as any).postsToDisplay
          .referencedPostList
        if (allPosts <= 1) return []
        const postsToDisplay = cloneDeep(allPosts)
        if (referencedPostList) {
          return (postsToDisplay as ContentStackPost[])
            .map(refPost => curatedFeatureFactory(refPost, this.readMoreLabel))
            .slice(1, 4)
        } else {
          return postsToDisplay.slice(1, 4) as CuratedFeatureProps[]
        }
      },
      curatedFeaturesRest(): CuratedFeatureProps[] {
        const allPosts = (this as any).postsToDisplay.posts
        const referencedPostList = (this as any).postsToDisplay
          .referencedPostList
        if (allPosts <= 4) return []
        const postsToDisplay = cloneDeep(allPosts)
        if (referencedPostList) {
          return postsToDisplay
            .map(refPost => curatedFeatureFactory(refPost, this.readMoreLabel))
            .slice(4, 99) as CuratedFeatureProps[]
        } else {
          return postsToDisplay.slice(3, 99) as CuratedFeatureProps[]
        }
      }
    }
  })
</script>
